import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useTranslation } from 'react-i18next'
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Switch } from "./ui/switch"

import { TiptapHtmlEditor, type TiptapHtmlEditorRef } from "./ui/tiptap-html-editor"
import { HtmlContentRenderer } from "./ui/html-content-renderer"
import { Checkbox } from "./ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs"
import { X, Home, Users, Lock, ChevronDown, Eye, Reply, Filter, ArrowDown, RefreshCw } from 'lucide-react'
import { cn, sxToStyleTag, mergeSx, camelToKebab } from "../lib/utils"
import { useMessageComponents } from "./message-components-provider"
import { initializeI18n } from "../lib/i18n"
import { useMessageApi } from "../hooks/use-message-api"
import type { Message, RelatedEntity, EntityChatPanelProps, User } from "../types"



// Display-label overrides for entity types whose generic camelCase split reads poorly
// (acronym casing, redundant words). Anything not listed falls back to the splitter.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  AtsPlacementRequest: 'ATS Placement',
  VmsRequest: 'VMS Request',
}

// Component for rendering individual messages with thread support
interface MessageThreadProps {
  message: Message
  selectedCount: number
  user: User
  messageTab: string
  activeReplyIds: Set<string>
  replyMessages: Map<string, string>
  setReplyMessage: (messageId: string, message: string) => void
  replyImportance: Map<string, boolean>
  setReplyImportance: (messageId: string, isImportant: boolean) => void

  replyHtmlEditorRef: React.RefObject<TiptapHtmlEditorRef | null>
  onStartReply: (messageId: string) => void
  onCancelReply: (messageId: string) => void
  sendReply: (parentId: string) => void
  getRelativeTime: (date: Date | string) => string
  getExactTimestamp: (date: Date | string) => string
  formatEntityBadge: (entityTypeId: string, entityId: string) => string
  getInitials: (name: string) => string
  hasCurrentEntitySelected: boolean
  hasAnyEntitySelected: boolean
  currentEntityKey: string
  depth?: number
  t: (key: string, options?: any) => string
}

const MessageThread = React.forwardRef<HTMLDivElement, MessageThreadProps>(({
  message,
  selectedCount,
  user,
  messageTab,
  activeReplyIds,
  replyMessages,
  setReplyMessage,
  replyImportance,
  setReplyImportance,

  replyHtmlEditorRef,
  onStartReply,
  onCancelReply,
  sendReply,
  getRelativeTime,
  getExactTimestamp,
  formatEntityBadge,
  getInitials,
  hasCurrentEntitySelected,
  hasAnyEntitySelected,
  currentEntityKey,
  depth = 0,
  t
}, ref) => {
  const maxDepth = 1 // Only allow 1 level of nesting
  const isReplyingToThis = activeReplyIds.has(message.id)
  const replyInputAreaRef = useRef<HTMLDivElement>(null)

  // Check if this message belongs to the current entity
  const messageEntityKey = `${message.entityTypeId}:${message.entityId}`
  const isCurrentEntityMessage = messageEntityKey === currentEntityKey



  return (
    <div
      key={message.id}
      data-message-id={message.id}
      ref={ref}
      className={cn(
        "entity-chat-panel-message-thread",
        depth > 0 && "entity-chat-panel-reply-message ml-12 border-l-2 border-muted pl-4 mt-2"
      )}
    >
      {/* Main Message */}
      <div
        className="entity-chat-panel-message"
        data-important={message.isImportant}
      >
        {/* Avatar */}
        <div className="entity-chat-panel-message-avatar">
          {getInitials(message.authorName)}
        </div>

        {/* Message content */}
        <div className="entity-chat-panel-message-content">
          <div className="entity-chat-panel-message-header">
            <div>
              <span className="entity-chat-panel-message-author">{message.authorName}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="entity-chat-panel-message-time">
                    {getRelativeTime(message.createdDateTime)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getExactTimestamp(message.createdDateTime)}</p>
                </TooltipContent>
              </Tooltip>
              {/* Show entity badge only when multiple entities are selected */}
              {selectedCount > 1 && (
                <Badge
                  variant="outline"
                  className={cn(
                    "entity-chat-panel-message-entity-badge",
                    "entity-chat-panel-message-entity-pill",
                    `entity-chat-panel-message-entity-${message.entityTypeId.toLowerCase()}-pill`
                  )}
                >
                  {formatEntityBadge(message.entityTypeId, message.entityId)}
                </Badge>
              )}
              {/* Show visibility badge - only show Client badge for internal users, internal is default */}
              {user.type === "internal" && message.isVisibleToClient && (
                <Badge variant="secondary" className="entity-chat-panel-message-visibility-badge">
                  <Eye className="entity-chat-panel-message-visibility-icon" />
                  {t('visibility.client')}
                </Badge>
              )}
            </div>
            {/* Show migrated badge and/or importance icon - migrated first, importance always on far right */}
            <div className="entity-chat-panel-message-indicators">
              {message.originalNoteId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="entity-chat-panel-message-migrated-wrapper">
                      <Badge variant="secondary" className="entity-chat-panel-message-migrated-badge">
                        M
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.migrated')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {message.isImportant && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="entity-chat-panel-message-importance-wrapper">
                      <span className="entity-chat-panel-message-importance-icon">
                        !
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.important')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="entity-chat-panel-message-text">
            <HtmlContentRenderer
              content={message.content}
              contentType={message.contentType || "html"}
            />
          </div>


        </div>
      </div>

      {/* Replies */}
      {depth < maxDepth && message.replies && message.replies.length > 0 && (
        <div className="entity-chat-panel-replies">
          {message.replies.map((reply) => (
            <MessageThread
              key={reply.id}
              message={reply}
              selectedCount={selectedCount}
              user={user}
              messageTab={messageTab}
              activeReplyIds={activeReplyIds}
              replyMessages={replyMessages}
              setReplyMessage={setReplyMessage}
              replyImportance={replyImportance}
              setReplyImportance={setReplyImportance}

              replyHtmlEditorRef={replyHtmlEditorRef}
              onStartReply={onStartReply}
              onCancelReply={onCancelReply}
              sendReply={sendReply}
              getRelativeTime={getRelativeTime}
              getExactTimestamp={getExactTimestamp}
              formatEntityBadge={formatEntityBadge}
              getInitials={getInitials}
              hasCurrentEntitySelected={hasCurrentEntitySelected}
              hasAnyEntitySelected={hasAnyEntitySelected}
              currentEntityKey={currentEntityKey}
              depth={depth + 1}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Reply Button and Input - only show on root messages (depth 0) and position after all replies */}
      {depth === 0 && (
        <div className="entity-chat-panel-thread-reply-section">
          {/* Reply Button - only show when NOT replying to this message and message belongs to current entity */}
          {!activeReplyIds.has(message.id) && isCurrentEntityMessage && (
            <div className="entity-chat-panel-message-actions">
              <Button
                variant="ghost"
                size="sm"
                className="entity-chat-panel-reply-button"
                onClick={() => onStartReply(message.id)}
                disabled={!hasAnyEntitySelected || !hasCurrentEntitySelected || (user.type === "internal" && messageTab === "all")}
              >
                <Reply className="entity-chat-panel-reply-icon" />
                {t('messages.reply')}
              </Button>
            </div>
          )}

          {/* Inline Reply Input - only show for current entity messages */}
          {activeReplyIds.has(message.id) && isCurrentEntityMessage && (
            <div ref={replyInputAreaRef} className="entity-chat-panel-reply-input-area" data-replying-to={message.id}>
              <TiptapHtmlEditor
                ref={replyHtmlEditorRef as React.Ref<TiptapHtmlEditorRef>}
                value={replyMessages.get(message.id) || ""}
                onChange={(value) => setReplyMessage(message.id, value)}
                onSend={() => sendReply(message.id)}
                placeholder={
                  user.type === "client"
                    ? t('messages.replyPlaceholder')
                    : messageTab === "internal"
                      ? t('messages.replyPlaceholderInternal')
                      : t('messages.replyPlaceholderClient')
                }
                disabled={!hasAnyEntitySelected || !hasCurrentEntitySelected || (user.type === "internal" && messageTab === "all")}
                className="entity-chat-panel-reply-html-editor"
                showSendButton={true}
                isImportant={replyImportance.get(message.id) || false}
                onImportanceChange={(isImportant) => setReplyImportance(message.id, isImportant)}
              />
              <div className="entity-chat-panel-reply-actions">
                <button type="button"
                  onClick={() => onCancelReply(message.id)}
                  className="entity-chat-panel-reply-cancel-link"
                >
                  {t('messages.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

MessageThread.displayName = 'MessageThread'

export default function EntityChatPanel({
  entityTypeId,
  entityId,
  onClose,
  className,
  sx,
  language = "en",
  customTranslations,
  onMessageCountChanged,
  title,
  showCloseButton = false,
  internalOnly = false,
  showRelatedEntities = true,
  autoIncludeRelatedTypes = [],
}: EntityChatPanelProps) {
  // Initialize i18n synchronously before any hooks to ensure consistent useTranslation behavior
  initializeI18n(language, customTranslations)

  const [messages, setMessages] = useState<Message[]>([])
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntity[]>([])
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set())
  const [newMessage, setNewMessage] = useState("")
  const [newMessageIsImportant, setNewMessageIsImportant] = useState(false)
  const [messageTab, setMessageTab] = useState<"internal" | "client" | "all">("internal")
  const [showLoading, setShowLoading] = useState(false)
  const [showImportantOnly, setShowImportantOnly] = useState(false)

  const [user, setUser] = useState<User | null>(null)
  const [activeReplyIds, setActiveReplyIds] = useState<Set<string>>(new Set())
  const [replyMessages, setReplyMessages] = useState<Map<string, string>>(new Map())
  const [replyImportance, setReplyImportance] = useState<Map<string, boolean>>(new Map())
  const [messageCounts, setMessageCounts] = useState({ internal: 0, client: 0, all: 0 })
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [isSendingReply, setIsSendingReply] = useState(false)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const lastReportedCountRef = useRef<{ entityId: string; count: number } | null>(null)

  const htmlEditorRef = useRef<TiptapHtmlEditorRef>(null)
  const replyHtmlEditorRef = useRef<TiptapHtmlEditorRef>(null)
  const { config } = useMessageComponents()
  const { userToken, sx: providerSx } = config
  const { t } = useTranslation()

  // Merge provider sx with component sx (component sx has higher priority)
  const mergedSx = useMemo(() => mergeSx(providerSx, sx), [providerSx, sx])

  // Get API service instance
  const apiService = useMessageApi()

  // Current entity key for comparison
  const currentEntityKey = `${entityTypeId}:${entityId}`

  // Helper function to update reply message for specific message ID
  const updateReplyMessage = useCallback((messageId: string, content: string) => {
    setReplyMessages(prev => {
      const newMap = new Map(prev)
      if (content.trim()) {
        newMap.set(messageId, content)
      } else {
        newMap.delete(messageId)
      }
      return newMap
    })
  }, [])

  // Helper function to update reply importance for specific message ID
  const updateReplyImportance = useCallback((messageId: string, isImportant: boolean) => {
    setReplyImportance(prev => {
      const newMap = new Map(prev)
      newMap.set(messageId, isImportant)
      return newMap
    })
  }, [])

  // Helper function to check if user is at bottom of messages
  const isAtBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return true

    const threshold = 100 // pixels from bottom to consider "at bottom"
    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold
  }, [])

  // Helper function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [])

  // Generate CSS styles for popover elements that can be applied globally
  const generatePopoverStyles = (mergedSx: any, instanceClass: string): string => {
    if (!mergedSx) return ''

    let css = ''

    // Extract and apply popover-specific styles
    const popoverSelectors = [
      '.entity-chat-panel-filter-popover.entity-chat-ui-popover-content',
      '.entity-chat-panel-filter-title',
      '.entity-chat-panel-filter-description',
      '.entity-chat-panel-filter-option-text',
      '.entity-chat-panel-filter-option'
    ]

    for (const selector of popoverSelectors) {
      const styles = mergedSx[`& ${selector}`]
      if (styles && typeof styles === 'object') {
        // Handle popover container differently - it has the data attribute itself
        if (selector === '.entity-chat-panel-filter-popover.entity-chat-ui-popover-content') {
          css += `[data-theme-instance="${instanceClass}"]${selector} {\n`
        } else {
          // Generate CSS for child elements using descendant selector
          css += `[data-theme-instance="${instanceClass}"] ${selector} {\n`
        }

        for (const [key, value] of Object.entries(styles)) {
          if (typeof value !== 'object') {
            const cssProperty = camelToKebab(key)
            // Remove !important from values since we're using inline styles
            const cleanValue = typeof value === 'string' ? value.replace(' !important', '') : value
            css += `  ${cssProperty}: ${cleanValue} !important;\n`
          } else if (key === '&:hover') {
            // Handle hover states
            css += `}\n[data-theme-instance="${instanceClass}"] ${selector}:hover {\n`
            for (const [hoverKey, hoverValue] of Object.entries(value as any)) {
              const cssProperty = hoverKey.replace(/([A-Z])/g, '-$1').toLowerCase()
              const cleanValue = typeof hoverValue === 'string' ? hoverValue.replace(' !important', '') : hoverValue
              css += `  ${cssProperty}: ${cleanValue} !important;\n`
            }
          }
        }
        css += '}\n'
      }
    }


    return css
  }


  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.fetchUserWithToken()
        setUser(userData)
      } catch (error) {
        // Error is already logged in the service
      }
    }

    if (userToken) {
      fetchUser()
    }
  }, [userToken, apiService])

  // Initialize selected entities when props change
  useEffect(() => {
    setSelectedEntities(new Set([currentEntityKey]))
  }, [currentEntityKey])

  // When showRelatedEntities is false, ensure only current entity is selected
  useEffect(() => {
    if (!showRelatedEntities) {
      setSelectedEntities(new Set([currentEntityKey]))
    }
  }, [showRelatedEntities, currentEntityKey])

  // Track if auto-include has already been applied during this mount cycle
  const autoIncludeAppliedRef = useRef(false)

  // Auto-include related entities of specified types when relatedEntities are fetched (only once per open)
  useEffect(() => {
    if (autoIncludeAppliedRef.current) return
    if (autoIncludeRelatedTypes.length === 0 || relatedEntities.length === 0) return

    const autoIncludeSet = new Set(autoIncludeRelatedTypes)
    const entitiesToAutoInclude = relatedEntities
      .filter((entity) => autoIncludeSet.has(entity.entityTypeId))
      .map((entity) => `${entity.entityTypeId}:${entity.entityId}`)

    if (entitiesToAutoInclude.length > 0) {
      setSelectedEntities((prev) => {
        const newSet = new Set(prev)
        entitiesToAutoInclude.forEach((key) => {
          newSet.add(key)
        })
        return newSet
      })
      autoIncludeAppliedRef.current = true
    }
  }, [relatedEntities, autoIncludeRelatedTypes])

  // When internalOnly is true, reset tab to internal if currently on client or all
  useEffect(() => {
    if (internalOnly && (messageTab === "client" || messageTab === "all")) {
      setMessageTab("internal")
    }
  }, [internalOnly, messageTab])


  // Handle scroll events to show/hide scroll to bottom button
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      setShowScrollToBottom(!isAtBottom())
    }

    // Check initial state
    handleScroll()

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [isAtBottom])

  // Update scroll to bottom button visibility when messages or filters change
  useEffect(() => {
    // Check immediately - React should have updated the DOM by now
    setShowScrollToBottom(!isAtBottom())
  }, [messages, showImportantOnly, messageTab, isAtBottom])

  // Clear reply sending flag when user changes context (tabs/filters)
  useEffect(() => {
    setIsSendingReply(false)
  }, [messageTab, showImportantOnly])

  // Fetch related entities when component mounts or entity changes
  useEffect(() => {
    if (user && showRelatedEntities) {
      fetchRelatedEntities()
    }
  }, [entityTypeId, entityId, user, showRelatedEntities])

  // Fetch messages when selected entities change
  useEffect(() => {
    if (user) {
      fetchMessages()
    }
  }, [selectedEntities, user])

  async function fetchRelatedEntities() {
    if (!user) return

    try {
      const relatedEntitiesData = await apiService.fetchRelatedEntitiesWithToken(
        entityTypeId,
        entityId
      )
      setRelatedEntities(relatedEntitiesData)
    } catch (error) {
      // Error is already logged in the service
    }
  }

  async function fetchMessages() {
    if (!user) return

    // Handle case where no entities are selected
    if (selectedEntities.size === 0) {
      setMessages([]) // Clear messages when no entities selected

      // Still update the message count for the current entity even when no entities are selected
      if (onMessageCountChanged) {
        try {
          const count = await apiService.fetchMessageCountWithToken(entityTypeId, entityId)
          const last = lastReportedCountRef.current
          if (last === null || last.entityId !== entityId || last.count !== count) {
            lastReportedCountRef.current = { entityId, count }
            onMessageCountChanged(count)
          }
        } catch (error) {
          // Error already logged in service
        }
      }
      return
    }

    // Only show loading indicator after a delay to prevent flash
    const loadingTimeout = setTimeout(() => {
      setShowLoading(true)
    }, 200)

    try {
      const entities = Array.from(selectedEntities)
        .map((entityKey) => {
          const [typeId, id] = entityKey.split(":")
          return { entityTypeId: typeId, entityId: id }
        })

      const messagesData = await apiService.fetchMessagesWithToken(entities)
      setMessages(messagesData)

      // Always update trigger with accurate message count for the current entity
      // Use dedicated API call to ensure we get the correct count regardless of panel filter state
      if (onMessageCountChanged) {
        try {
          const count = await apiService.fetchMessageCountWithToken(entityTypeId, entityId)
          const last = lastReportedCountRef.current
          if (last === null || last.entityId !== entityId || last.count !== count) {
            lastReportedCountRef.current = { entityId, count }
            onMessageCountChanged(count)
          }
        } catch (error) {
          // Error already logged in service
        }
      }
    } catch (error) {
      // Error is already logged in the service
    } finally {
      clearTimeout(loadingTimeout)
      setShowLoading(false)
    }
  }

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || messageTab === "all" || !user) return

    // Always send to the current entity only
    const messageData = {
      content: newMessage,
      contentType: "html" as const, // DevExtreme HTML Editor always produces HTML
      entityTypeId,
      entityId,
      isVisibleToClient: user.type === "client" ? true : messageTab === "client",
      isImportant: newMessageIsImportant,
    }

    try {
      // Clear reply flag to ensure normal auto-scroll behavior for new messages
      setIsSendingReply(false)

      await apiService.sendMessageWithToken(messageData)
      // Refresh messages to get the threaded structure
      const promises = [fetchMessages()]
      // Refresh related entities to update message counts in dropdown
      if (showRelatedEntities) {
        promises.push(fetchRelatedEntities())
      }
      await Promise.all(promises)
      setNewMessage("")
      setNewMessageIsImportant(false)
      // Clear the HTML editor as well
      htmlEditorRef.current?.clear()
    } catch (error) {
      // Error is already logged in the service
    }
  }, [newMessage, newMessageIsImportant, messageTab, user, entityTypeId, entityId, apiService, fetchMessages, fetchRelatedEntities])

  const sendReply = async (parentId: string) => {
    const replyContent = replyMessages.get(parentId) || ""
    if (!replyContent.trim() || messageTab === "all" || !user) return

    const messageData = {
      content: replyContent,
      contentType: "html" as const, // DevExtreme HTML Editor always produces HTML
      entityTypeId,
      entityId,
      isVisibleToClient: user.type === "client" ? true : messageTab === "client",
      isImportant: replyImportance.get(parentId) || false,
      parentId,
    }

    try {
      // Set flag to prevent auto-scroll to bottom during reply sending
      setIsSendingReply(true)

      await apiService.sendMessageWithToken(messageData)
      // Clear state immediately, then refresh in parallel
      setReplyMessages(prev => {
        const newMap = new Map(prev)
        newMap.delete(parentId)
        return newMap
      })
      setReplyImportance(prev => {
        const newMap = new Map(prev)
        newMap.delete(parentId)
        return newMap
      })
      setActiveReplyIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(parentId)
        return newSet
      })
      replyHtmlEditorRef.current?.clear()
      const promises: Promise<void>[] = [fetchMessages()]
      if (showRelatedEntities) promises.push(fetchRelatedEntities())
      await Promise.all(promises)

      // Scroll to show the new reply at the bottom of the parent message thread
      setTimeout(() => {
        // Find the parent message thread container
        const parentMessageElement = document.querySelector(`[data-message-id="${parentId}"]`)
        if (parentMessageElement && messagesContainerRef.current) {
          const container = messagesContainerRef.current

          // Find the bottom of the entire thread (parent + all replies)
          const threadBottom = (parentMessageElement as HTMLElement).offsetTop +
            (parentMessageElement as HTMLElement).offsetHeight

          // Scroll to show the bottom of the thread (where the new reply is)
          // with some padding so the new reply is comfortably visible
          container.scrollTo({
            top: Math.max(0, threadBottom - container.clientHeight + 100), // 100px padding from bottom
            behavior: 'smooth'
          })
        }

        // Clear the flag after scrolling is set up
        setIsSendingReply(false)
      }, 200)
    } catch (error) {
      // Error is already logged in the service
      setIsSendingReply(false) // Clear flag on error too
    }
  }

  const handleStartReply = (messageId: string) => {
    setActiveReplyIds(prev => new Set(prev).add(messageId))
    // Focus the reply editor and scroll into view after it renders
    setTimeout(() => {
      replyHtmlEditorRef.current?.focus()

      // Scroll the reply area into view (find the reply input area for this specific message)
      const replyInputArea = document.querySelector(`[data-replying-to="${messageId}"]`)
      if (replyInputArea && messagesContainerRef.current) {
        // Use container scrolling for both popup and embedded contexts
        const container = messagesContainerRef.current
        const containerRect = container.getBoundingClientRect()
        const elementRect = replyInputArea.getBoundingClientRect()

        // Check if element is already visible in container
        const isVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom

        if (!isVisible) {
          const elementOffsetTop = (replyInputArea as HTMLElement).offsetTop - container.offsetTop
          container.scrollTo({
            top: elementOffsetTop - 100, // 100px padding from top
            behavior: 'smooth'
          })
        }
      }
    }, 150)
  }

  const handleCancelReply = (messageId: string) => {
    // Remove this message from active replies
    setActiveReplyIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(messageId)
      return newSet
    })
    // Clear the specific reply message
    setReplyMessages(prev => {
      const newMap = new Map(prev)
      newMap.delete(messageId)
      return newMap
    })
    // Clear the reply importance
    setReplyImportance(prev => {
      const newMap = new Map(prev)
      newMap.delete(messageId)
      return newMap
    })
    // Note: Don't clear replyHtmlEditorRef as it's shared across all editors
    // The content is managed through the replyMessages Map state
  }



  const handleEntityToggle = (entityType: string) => {
    const newSelected = new Set(selectedEntities)

    // Get all entities of this type
    const entitiesOfType = relatedEntities.filter(entity => entity.entityTypeId === entityType)
    const entityKeysOfType = entitiesOfType.map(entity => `${entity.entityTypeId}:${entity.entityId}`)

    // Check if any entity of this type is selected
    const hasAnyOfType = entityKeysOfType.some(key => newSelected.has(key))

    if (hasAnyOfType) {
      // Remove all entities of this type, but ensure current entity stays selected
      entityKeysOfType.forEach(key => {
        if (key !== currentEntityKey) {
          newSelected.delete(key)
        }
      })
    } else {
      // Add all entities of this type
      entityKeysOfType.forEach(key => newSelected.add(key))
    }

    setSelectedEntities(newSelected)
  }

  const handleSelectAll = () => {
    const allEntityKeys = relatedEntities.map(entity => `${entity.entityTypeId}:${entity.entityId}`)
    const isAllSelected = allEntityKeys.every(key => selectedEntities.has(key))

    if (isAllSelected) {
      // Instead of deselecting all, default back to just the current entity
      setSelectedEntities(new Set([currentEntityKey]))
    } else {
      // Select all
      setSelectedEntities(new Set(allEntityKeys))
    }
  }

  const filterMessage = (message: Message): boolean => {
    if (!user) return false

    // First filter by user type (client users only see client-visible messages)
    if (user.type === "client") {
      return message.isVisibleToClient
    }

    // For internal users, apply the message tab filter
    if (messageTab === "internal") {
      return !message.isVisibleToClient
    } else if (messageTab === "client") {
      return message.isVisibleToClient
    }

    // "all" - show everything for internal users
    return true
  }

  const filterMessagesRecursively = (messageList: Message[]): Message[] => {
    return messageList
      .filter(filterMessage)
      .map(message => ({
        ...message,
        replies: message.replies ? filterMessagesRecursively(message.replies) : []
      }))
  }

  // Function to check if a message or any of its replies are important
  const hasImportantMessage = (message: Message): boolean => {
    if (message.isImportant) return true
    if (message.replies && message.replies.length > 0) {
      return message.replies.some(reply => hasImportantMessage(reply))
    }
    return false
  }

  // Enhanced filtering function that handles importance filter with thread hierarchy
  const filterWithImportance = (messageList: Message[]): Message[] => {
    // First apply regular message type filtering (internal/client/all)
    const typeFiltered = messageList
      .filter(filterMessage)
      .map(message => ({
        ...message,
        replies: message.replies ? filterMessagesRecursively(message.replies) : []
      }))

    // If importance filter is not active, return type-filtered messages
    if (!showImportantOnly) {
      return typeFiltered
    }

    // Apply importance filtering with thread hierarchy
    return typeFiltered
      .filter(message => hasImportantMessage(message))
      .map(message => ({
        ...message,
        replies: message.replies ? filterWithImportance(message.replies) : []
      }))
  }

  // Helper function to count all messages including replies
  const countMessagesRecursively = (messageList: Message[]): number => {
    let count = 0
    messageList.forEach(message => {
      count += 1 // Count the message itself
      if (message.replies && message.replies.length > 0) {
        count += countMessagesRecursively(message.replies) // Count replies recursively
      }
    })
    return count
  }

  // Calculate tab-specific message counts
  const calculateMessageCounts = (allMessages: Message[]) => {
    if (!user) return { internal: 0, client: 0, all: 0 }

    const counts = { internal: 0, client: 0, all: 0 }

    // Count all messages including replies
    counts.all = countMessagesRecursively(allMessages)

    // For internal users, calculate internal and client counts
    if (user.type === 'internal') {
      const countByVisibility = (visible: boolean) => {
        const filtered = allMessages
          .filter(msg => msg.isVisibleToClient === visible)
          .map(msg => ({
            ...msg,
            replies: msg.replies ? msg.replies.filter(reply => reply.isVisibleToClient === visible) : []
          }))
        return countMessagesRecursively(filtered)
      }
      counts.internal = countByVisibility(false)
      counts.client = countByVisibility(true)
    } else {
      // For client users, only client messages are visible
      counts.client = counts.all
      counts.internal = 0
    }

    return counts
  }

  const filteredMessages = filterWithImportance(messages)

  // Track when we should scroll to bottom
  const shouldScrollToBottom = useRef(false)

  // Set flag when filtered messages change
  useEffect(() => {
    if (isSendingReply) return
    if (filteredMessages.length > 0) {
      shouldScrollToBottom.current = true
    }
  }, [filteredMessages.length, messageTab, showImportantOnly, isSendingReply])

  // Function to attempt scrolling (reusable for both callback ref and visibility change)
  const attemptScroll = useCallback(() => {
    if (!messagesContainerRef.current || !shouldScrollToBottom.current) return

    // Check if container is actually visible and has dimensions
    const rect = messagesContainerRef.current.getBoundingClientRect()
    const hasValidDimensions = rect.width > 0 && rect.height > 0
    const hasValidScrollHeight = messagesContainerRef.current.scrollHeight > 0

    if (hasValidDimensions && hasValidScrollHeight) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
      shouldScrollToBottom.current = false
      return true // Successfully scrolled
    }

    return false // Couldn't scroll yet
  }, [])

  // Callback ref for when the last message is rendered
  const lastMessageRef = useCallback((node: HTMLDivElement | null) => {
    if (node && shouldScrollToBottom.current) {
      attemptScroll()
    }
  }, [attemptScroll])

  // Track previous dimensions to detect hidden -> visible transitions
  const previousDimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })

  // ResizeObserver to detect when the container becomes visible (not just resized)
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry && shouldScrollToBottom.current) {
        const { width, height } = entry.contentRect
        const prevDimensions = previousDimensionsRef.current

        // Only scroll when transitioning from invalid (0) to valid dimensions (hidden -> visible)
        // Don't scroll on normal resizing (valid -> different valid dimensions)
        const wasHidden = prevDimensions.width === 0 || prevDimensions.height === 0
        const isNowVisible = width > 0 && height > 0

        if (wasHidden && isNowVisible) {
          // Container became visible, try to scroll
          setTimeout(() => attemptScroll(), 100)
        }

        // Update previous dimensions
        previousDimensionsRef.current = { width, height }
      }
    })

    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [attemptScroll])

  // Fallback: Also add a visibility change listener for when the entire document becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && shouldScrollToBottom.current) {
        // Document became visible (user switched back to tab), try to scroll
        setTimeout(() => attemptScroll(), 150)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [attemptScroll])

  // Update internal message counts for tab badges when messages change
  useEffect(() => {
    const counts = calculateMessageCounts(messages)
    setMessageCounts(counts)
  }, [messages, user])

  const getRelativeTime = (date: Date | string) => {
    const now = new Date()
    const messageDate = new Date(date)
    const diffInMs = now.getTime() - messageDate.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    const diffInMonths = Math.floor(diffInDays / 30)
    const diffInYears = Math.floor(diffInDays / 365)

    if (diffInMinutes < 1) {
      return t('time.justNow')
    } else if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? t('time.minuteAgo', { count: diffInMinutes }) : t('time.minutesAgo', { count: diffInMinutes })
    } else if (diffInHours < 24) {
      return diffInHours === 1 ? t('time.hourAgo', { count: diffInHours }) : t('time.hoursAgo', { count: diffInHours })
    } else if (diffInDays < 30) {
      return diffInDays === 1 ? t('time.dayAgo', { count: diffInDays }) : t('time.daysAgo', { count: diffInDays })
    } else if (diffInMonths < 12) {
      return diffInMonths === 1 ? t('time.monthAgo', { count: diffInMonths }) : t('time.monthsAgo', { count: diffInMonths })
    } else {
      return diffInYears === 1 ? t('time.yearAgo', { count: diffInYears }) : t('time.yearsAgo', { count: diffInYears })
    }
  }

  const exactTimestampFormatter = useMemo(
    () => new Intl.DateTimeFormat(language || 'en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    }),
    [language]
  )
  const getExactTimestamp = (date: Date | string) => exactTimestampFormatter.format(new Date(date))

  const formatEntityType = (entityTypeId: string) => {
    return ENTITY_TYPE_LABELS[entityTypeId] ?? entityTypeId.replace(/([A-Z])/g, ' $1').trim()
  }

  // Friendly identifiers supplied by the related-entities endpoint (e.g. ATS placement id,
  // VMS external request id) — used instead of the raw (often GUID) entityId on message pills.
  const entityDisplayIds = useMemo(() => {
    const map = new Map<string, string>()
    for (const entity of relatedEntities) {
      if (entity.displayName) map.set(`${entity.entityTypeId}:${entity.entityId}`, entity.displayName)
    }
    return map
  }, [relatedEntities])

  const formatEntityBadge = useCallback((entityTypeId: string, entityId: string) => {
    const displayId = entityDisplayIds.get(`${entityTypeId}:${entityId}`)
    return displayId
      ? `${formatEntityType(entityTypeId)} - ${displayId}`
      : `${formatEntityType(entityTypeId)} ${entityId}`
  }, [entityDisplayIds])

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const selectedCount = selectedEntities.size
  const allEntityKeys = relatedEntities.map(entity => `${entity.entityTypeId}:${entity.entityId}`)
  // For single entity case, "All" should be checked when that single entity is selected
  const isAllSelected = allEntityKeys.length > 0 && allEntityKeys.every(key => selectedEntities.has(key))
  const hasCurrentEntitySelected = selectedEntities.has(currentEntityKey)
  const messageCount = filteredMessages.length

  // Get display text for dropdown trigger
  const getDropdownDisplayText = () => {
    if (selectedCount === 0) {
      return t('filters.selectEntitiesPlaceholder')
    }

    // Get unique entity types that are selected
    const selectedEntityTypes = new Set<string>()
    selectedEntities.forEach(entityKey => {
      const [entityType] = entityKey.split(":")
      selectedEntityTypes.add(entityType)
    })

    if (selectedEntityTypes.size === 1) {
      const entityType = Array.from(selectedEntityTypes)[0]
      return formatEntityType(entityType)
    } else {
      return t('filters.typesSelected', { count: selectedEntityTypes.size })
    }
  }

  // Show panel even when user is loading, but hide user-dependent content

  // Use passed className or generate stable class for this instance
  const instanceClass = className || `entity-chat-panel-${entityTypeId}-${entityId}`

  return (
    <div
      className={cn("entity-chat-panel-container", instanceClass)}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Generate CSS for nested selectors */}
      {mergedSx && (
        <style dangerouslySetInnerHTML={{
          __html: sxToStyleTag(mergedSx, instanceClass)
        }} />
      )}

      <TooltipProvider>
        {/* Chat Panel Content */}
        <div
          className={cn("entity-chat-panel", instanceClass)}
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden' // Ensure scrolling stays within this container
          }}
        >
          {/* Header */}
          <div
            className="entity-chat-panel-header"
            style={{
              flexShrink: 0,
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: showCloseButton ? 'space-between' : 'flex-start'
            }}
          >
            <h1 className="entity-chat-panel-title">
              {title || `${formatEntityType(entityTypeId)} ${entityId}`}
            </h1>
            {showCloseButton && (
              <Button variant="ghost" size="icon" onClick={onClose} className="entity-chat-panel-close-button">
                <X className="entity-chat-panel-close-icon" />
              </Button>
            )}
          </div>

          {/* Message Type Tabs - only for internal users */}
          {user && user.type === "internal" && (
            <div
              className="entity-chat-panel-tabs-container">
              <Tabs value={messageTab} onValueChange={(value) => setMessageTab(value as "internal" | "client" | "all")}>
                <TabsList className="entity-chat-panel-tabs-list">
                  <TabsTrigger value="internal" className="entity-chat-panel-tab entity-chat-panel-tab-internal">
                    <Lock className="entity-chat-panel-tab-icon" />
                    {t('tabs.internal')}
                    <Badge variant="secondary" className="entity-chat-panel-tab-count">
                      {messageCounts.internal}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="client"
                    className="entity-chat-panel-tab entity-chat-panel-tab-client"
                    disabled={internalOnly}
                  >
                    <Eye className="entity-chat-panel-tab-icon" />
                    {t('tabs.client')}
                    <Badge variant="secondary" className="entity-chat-panel-tab-count">
                      {messageCounts.client}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="all"
                    className="entity-chat-panel-tab entity-chat-panel-tab-all"
                    disabled={internalOnly}
                  >
                    <Users className="entity-chat-panel-tab-icon" />
                    {t('tabs.all')}
                    <Badge variant="secondary" className="entity-chat-panel-tab-count">
                      {messageCounts.all}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Sub-header with message count and entity filter */}
          <div
            className="entity-chat-panel-sub-header"
            style={{
              flexShrink: 0,
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#f9fafb'
            }}
          >
            {!user ? (
              <div className="entity-chat-panel-loading">{t('messages.loading')}</div>
            ) : (
              <>
                <div className="entity-chat-panel-left-section">
                  <div className="entity-chat-panel-importance-filter">
                    <label
                      htmlFor="importance-filter"
                      className="entity-chat-panel-importance-label"
                      title={showImportantOnly ? t('filters.showAllMessages') : t('filters.showImportantOnly')}
                    >
                      <Filter size={16} className="entity-chat-panel-filter-icon" />
                      {t('filters.showImportant')}
                    </label>
                    <Switch
                      checked={showImportantOnly}
                      onCheckedChange={setShowImportantOnly}
                      id="importance-filter"
                      className="entity-chat-panel-importance-switch"
                    />
                    <span className="entity-chat-panel-importance-icon">!</span>
                  </div>

                  <div className="entity-chat-panel-message-counter" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Home className="entity-chat-panel-counter-icon" />
                    <span className="entity-chat-panel-counter-text">{t('messages.counter', { count: messageCount })}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            // Set flag to scroll to bottom after refresh
                            shouldScrollToBottom.current = true
                            await fetchMessages()

                            // Fallback: Always attempt scroll after a frame delay
                            // If lastMessageRef handles it first, the flag will be cleared
                            // If not (demo scenario), this will ensure scrolling happens
                            requestAnimationFrame(() => {
                              if (shouldScrollToBottom.current) {
                                attemptScroll()
                              }
                            })
                          }}
                          className="entity-chat-panel-refresh-button"
                          style={{
                            padding: '4px',
                            height: 'auto',
                            minHeight: '24px'
                          }}
                        >
                          <RefreshCw className="entity-chat-panel-refresh-icon" size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('messages.refresh')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {showRelatedEntities && (
                  <div className="entity-chat-panel-filter-section">
                    <span className="entity-chat-panel-filter-label">{t('filters.relatedEntities')}</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="entity-chat-panel-filter-trigger">
                          <span className="entity-chat-panel-filter-trigger-text">{getDropdownDisplayText()}</span>
                          <ChevronDown className="entity-chat-panel-filter-trigger-icon" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="entity-chat-panel-filter-popover"
                        align="end"
                        style={{
                          zIndex: 1001
                        }}
                        side="bottom"
                        sideOffset={5}
                        data-theme-instance={instanceClass}
                      >
                        {/* Apply theme styles for popover elements */}
                        {mergedSx && (
                          <style dangerouslySetInnerHTML={{
                            __html: generatePopoverStyles(mergedSx, instanceClass)
                          }} />
                        )}
                        <div className="entity-chat-panel-filter-popover-header">
                          <h4 className="entity-chat-panel-filter-title">
                            {t('filters.selectEntities')}
                          </h4>
                          <p className="entity-chat-panel-filter-description">
                            {t('filters.selectEntitiesDescription')}
                          </p>
                        </div>
                        <div className="entity-chat-panel-filter-options">
                          {/* Select All option */}
                          <div
                            className={cn(
                              "flex items-center space-x-2 p-3 cursor-pointer border-b entity-chat-panel-filter-option entity-chat-panel-filter-select-all",
                              relatedEntities.length <= 1
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-muted/50"
                            )}
                            onClick={relatedEntities.length > 1 ? handleSelectAll : undefined}
                            role="checkbox"
                            aria-checked={isAllSelected}
                            aria-disabled={relatedEntities.length <= 1}
                            tabIndex={relatedEntities.length > 1 ? 0 : -1}
                            aria-label={t('filters.all')}
                            onKeyDown={(e) => {
                              if (relatedEntities.length > 1 && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                handleSelectAll()
                              }
                            }}
                          >
                            <Checkbox
                              checked={isAllSelected}
                              onCheckedChange={relatedEntities.length > 1 ? handleSelectAll : undefined}
                              disabled={relatedEntities.length <= 1}
                              className="entity-chat-panel-filter-checkbox"
                            />
                            <div className="entity-chat-panel-filter-option-content">
                              <span className="entity-chat-panel-filter-option-text">
                                {t('filters.all')}
                              </span>
                            </div>
                          </div>

                          {/* Group by entity type */}
                          {Object.entries(
                            relatedEntities.reduce((groups, entity) => {
                              const type = entity.entityTypeId
                              if (!groups[type]) {
                                groups[type] = {
                                  entities: [],
                                  totalMessages: 0
                                }
                              }
                              groups[type].entities.push(entity)
                              groups[type].totalMessages += entity.messageCount
                              return groups
                            }, {} as Record<string, { entities: RelatedEntity[], totalMessages: number }>)
                          ).map(([entityType, group]: [string, { entities: RelatedEntity[], totalMessages: number }]) => {
                            const entityKeysOfType = group.entities.map(entity => `${entity.entityTypeId}:${entity.entityId}`)
                            const isTypeSelected = entityKeysOfType.some(key => selectedEntities.has(key))
                            const isCurrent = group.entities.some(entity => `${entity.entityTypeId}:${entity.entityId}` === currentEntityKey)

                            return (
                              <div
                                key={entityType}
                                className="entity-chat-panel-filter-option entity-chat-panel-filter-entity-type"
                                onClick={() => handleEntityToggle(entityType)}
                                role="checkbox"
                                aria-checked={isTypeSelected}
                                tabIndex={0}
                                aria-label={formatEntityType(entityType)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleEntityToggle(entityType)
                                  }
                                }}
                              >
                                <Checkbox
                                  checked={isTypeSelected}
                                  onCheckedChange={() => handleEntityToggle(entityType)}
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                                  className="entity-chat-panel-filter-checkbox"
                                />
                                <div className="entity-chat-panel-filter-option-content">
                                  <div>
                                    <span className="entity-chat-panel-filter-option-text">
                                      {formatEntityType(entityType)}
                                    </span>
                                    {isCurrent && (
                                      <Badge variant="default" className="entity-chat-panel-filter-current-badge">
                                        {t('filters.current')}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <Badge variant="secondary" className="entity-chat-panel-filter-count-badge">
                                  {group.totalMessages}
                                </Badge>
                              </div>
                            )
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div
              ref={messagesContainerRef}
              className="entity-chat-panel-messages"
              style={{
                height: '100%',
                overflowY: 'auto',
                padding: '16px'
              }}
            >
              {!user ? (
                <div className="entity-chat-panel-loading">{t('messages.loading')}</div>
              ) : showLoading ? (
                <div className="entity-chat-panel-loading">{t('messages.loading')}</div>
              ) : selectedEntities.size === 0 ? (
                <div className="entity-chat-panel-empty">
                  {t('messages.noEntitiesSelected')}
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="entity-chat-panel-empty">
                  {t('messages.empty')}
                </div>
              ) : (
                filteredMessages.map((message, index) => (
                  <MessageThread
                    key={message.id}
                    message={message}
                    selectedCount={selectedCount}
                    user={user}
                    messageTab={messageTab}
                    activeReplyIds={activeReplyIds}
                    replyMessages={replyMessages}
                    setReplyMessage={updateReplyMessage}
                    replyImportance={replyImportance}
                    setReplyImportance={updateReplyImportance}
                    replyHtmlEditorRef={replyHtmlEditorRef}
                    onStartReply={handleStartReply}
                    onCancelReply={handleCancelReply}
                    sendReply={sendReply}
                    getRelativeTime={getRelativeTime}
                    getExactTimestamp={getExactTimestamp}
                    formatEntityBadge={formatEntityBadge}
                    getInitials={getInitials}
                    hasCurrentEntitySelected={hasCurrentEntitySelected}
                    hasAnyEntitySelected={selectedEntities.size > 0}
                    currentEntityKey={currentEntityKey}
                    t={t}
                    ref={index === filteredMessages.length - 1 ? lastMessageRef : undefined}
                  />
                ))
              )}
            </div>

            {/* Scroll to Bottom Button */}
            {showScrollToBottom && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={scrollToBottom}
                    className="entity-chat-panel-scroll-to-bottom"
                    size="sm"
                    variant="secondary"
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      right: '16px',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      zIndex: 10
                    }}
                  >
                    <ArrowDown className="entity-chat-panel-scroll-icon" size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('messages.scrollToBottom')}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Message Input Area - moved to bottom */}
          {user && (
            <div
              className="entity-chat-panel-input-area"
              style={{
                flexShrink: 0,
                padding: '16px',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              {/* Warning for current entity not selected */}
              {!hasCurrentEntitySelected && (
                <div className="entity-chat-panel-warning">
                  <strong>{t('input.warningNote')}</strong> {t('input.warningMessage', { entityKey: currentEntityKey })}
                </div>
              )}

              {/* Message input - Tiptap HTML Editor with streamlined interface */}
              <TiptapHtmlEditor
                ref={htmlEditorRef}
                value={newMessage}
                onChange={setNewMessage}
                onSend={sendMessage}
                placeholder={
                  user.type === "client"
                    ? t('input.placeholder')
                    : messageTab === "all"
                      ? t('input.placeholderAllTab')
                      : messageTab === "internal"
                        ? t('input.placeholderInternal')
                        : t('input.placeholderClient')
                }
                disabled={selectedEntities.size === 0 || !hasCurrentEntitySelected || (user.type === "internal" && messageTab === "all")}
                className="entity-chat-panel-html-editor"
                showSendButton={true}
                isImportant={newMessageIsImportant}
                onImportanceChange={setNewMessageIsImportant}
              />
            </div>
          )}
        </div>
      </TooltipProvider>
    </div>
  )
}
